import apiClient from '../utils/api';

/**
 * LLM Service Layer
 * Handles all LLM-related API calls with proper error handling and logging
 */
class LLMService {
    /**
     * Send a message to the LLM and get a response
     * @param {string} message - The user message
     * @param {string} clientType - LLM client type ('openai', 'ollama')
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} LLM response
     */
    static async sendMessage(message, clientType = 'openai', options = {}) {
        try {
            console.log(`LLM Service: Sending message to ${clientType}`, { message, options });
            
            const requestBody = {
                messages: [{ role: 'user', content: message }],
                client_type: clientType,
                ...options
            };
            
            const response = await apiClient.post('/api/chat/', requestBody);
            
            console.log(`LLM Service: Received response from ${clientType}`, response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Error:', error);
            throw new Error(`Failed to get LLM response: ${error.message}`);
        }
    }
    
    /**
     * Send a conversation to the LLM
     * @param {Array} messages - Array of message objects with role and content
     * @param {string} clientType - LLM client type
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} LLM response
     */
    static async sendConversation(messages, clientType = 'openai', options = {}) {
        try {
            console.log(`LLM Service: Sending conversation to ${clientType}`, { messages, options });
            
            const requestBody = {
                messages: messages,
                client_type: clientType,
                ...options
            };
            
            const response = await apiClient.post('/api/chat/', requestBody);
            
            console.log(`LLM Service: Received conversation response from ${clientType}`, response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Conversation Error:', error);
            throw new Error(`Failed to get LLM conversation response: ${error.message}`);
        }
    }
    
    /**
     * Get available LLM clients and their configurations
     * @returns {Promise<Object>} Available clients information
     */
    static async getAvailableClients() {
        try {
            console.log('LLM Service: Getting available clients');
            
            const response = await apiClient.get('/api/chat/clients');
            
            console.log('LLM Service: Available clients', response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Get Clients Error:', error);
            throw new Error(`Failed to get available clients: ${error.message}`);
        }
    }
    
    /**
     * Test LLM functionality
     * @param {string} clientType - LLM client type to test
     * @returns {Promise<Object>} Test result
     */
    static async testChat(clientType = 'openai') {
        try {
            console.log(`LLM Service: Testing chat with ${clientType}`);
            
            const response = await apiClient.post('/api/chat/test');
            
            console.log('LLM Service: Test result', response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Test Error:', error);
            throw new Error(`LLM test failed: ${error.message}`);
        }
    }
    
    /**
     * Generate a stock chart
     * @param {string} symbol - Stock symbol
     * @param {string} chartType - Type of chart ('price', 'technical')
     * @param {string} period - Time period for the chart
     * @returns {Promise<Object>} Chart data with base64 image
     */
    static async generateChart(symbol, chartType = 'price', period = '1y') {
        try {
            console.log(`LLM Service: Generating ${chartType} chart for ${symbol}`);
            
            const requestBody = {
                symbol: symbol,
                chart_type: chartType,
                period: period
            };
            
            const response = await apiClient.post('/api/charts/generate', requestBody);
            
            console.log(`LLM Service: Chart generated for ${symbol}`, response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Chart Generation Error:', error);
            throw new Error(`Failed to generate chart: ${error.message}`);
        }
    }
    
    /**
     * Generate a comparison chart for multiple stocks
     * @param {Array} symbols - Array of stock symbols
     * @param {string} period - Time period for the chart
     * @returns {Promise<Object>} Comparison chart data
     */
    static async generateComparisonChart(symbols, period = '1y') {
        try {
            console.log(`LLM Service: Generating comparison chart for ${symbols.join(', ')}`);
            
            const requestBody = {
                symbols: symbols,
                period: period
            };
            
            const response = await apiClient.post('/api/charts/compare', requestBody);
            
            console.log(`LLM Service: Comparison chart generated`, response);
            return response;
            
        } catch (error) {
            console.error('LLM Service Comparison Chart Error:', error);
            throw new Error(`Failed to generate comparison chart: ${error.message}`);
        }
    }
    
    /**
     * Send a message with specific model parameters
     * @param {string} message - The user message
     * @param {string} clientType - LLM client type
     * @param {string} modelName - Specific model name
     * @param {number} maxTokens - Maximum tokens for response
     * @param {number} temperature - Response temperature
     * @returns {Promise<Object>} LLM response
     */
    static async sendMessageWithParams(message, clientType = 'openai', modelName = null, maxTokens = null, temperature = null) {
        try {
            console.log(`LLM Service: Sending message with params to ${clientType}`, { 
                message, clientType, modelName, maxTokens, temperature 
            });
            
            const options = {};
            if (modelName) options.model_name = modelName;
            if (maxTokens) options.max_tokens = maxTokens;
            if (temperature) options.temperature = temperature;
            
            return await this.sendMessage(message, clientType, options);
            
        } catch (error) {
            console.error('LLM Service Params Error:', error);
            throw new Error(`Failed to send message with parameters: ${error.message}`);
        }
    }

    static async analyzePriceChart(priceData) {
        try {
            const response = await apiClient.post('/api/price-chart/analyze', priceData);
            return response; // API client returns parsed JSON directly
        } catch (error) {
            console.error('Price chart analysis API error:', error);
            throw new Error(error.response?.data?.detail || 'Failed to analyze price chart');
        }
    }
}

export default LLMService; 